import React, { useEffect, useRef, useState } from 'react';
import { Box, Divider, Text, useColorModeValue, usePrefersReducedMotion, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import smartAmpIconDark from '../../assets/images/smart_amp_dark.svg';
import smartAmpIconLight from '../../assets/images/smart_amp_light.svg';
import { AdvancedChargingDetails } from '../../common/components/AdvancedChargingDetails';
import { ChargingSpeedStatus } from '../../common/components/ChargingSpeedStatus';
import { DisclosureRow } from '../../common/components/DisclosureRow';
import { SlidingAnimationPageBase } from '../../common/components/SlidingAnimationPageBase';
import { COMPONENT_MIN_HEIGHT, TOP_NAV_SPACING_AFFORDANCE } from '../../common/constants';
import { useParamBackUrl } from '../../common/hooks/use-param-back-url';
import { selectEvChargerData } from '../ev-charger/dashboard/evChargerSlice';
import { selectMeterValues } from './evseStreamSlice';
import { getChargingSpeedPercentage } from './smart-device-helpers';
import { SmartDeviceStatus } from './SmartDeviceStatus';
import { useEvChargerDetails } from './useEvChargerDetails';
import { useSmartAmpSse } from './useSmartAmpSse';

/* istanbul ignore next -- @preserve */
export function SmartAmpHome() {
  const { t } = useTranslation();
  useEvChargerDetails();
  const backURL = useParamBackUrl('../device-status');
  const prefersReducedMotion = usePrefersReducedMotion();
  const { status } = useSmartAmpSse();
  const {
    output,
    power,
    charge_current: restChargingAmps,
    charge_current_request_max: restMaxAmps,
  } = useSelector(selectEvChargerData);
  const { kwInstantaneousPower, maxAmps, chargingAmps } = useSelector(selectMeterValues);
  const statusLabel =
    status === 'active'
      ? t('SmartAmp.active')
      : status === 'chargingLimited'
      ? t('SmartAmp.chargingLimited')
      : t('SmartAmp.inactive');
  const currentPower = kwInstantaneousPower ?? power ?? null;
  const chargePercentage = getChargingSpeedPercentage(currentPower, output);
  const cardBg = useColorModeValue('#f1f1f1', '#363E40');
  const smartAmpIcon = useColorModeValue(smartAmpIconLight, smartAmpIconDark);
  const labelColor = useColorModeValue('gray.500', '#CBCBCB');
  const detailsColor = useColorModeValue('gray.500', 'white');
  const dividerColor = useColorModeValue('rgba(151,151,151,0.3)', 'rgba(151,151,151,0.3)');
  const scrollFadeColor = useColorModeValue('rgba(255,255,255,0.95)', 'rgba(17,17,17,0.95)');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);
  const [openDisclosure, setOpenDisclosure] = useState<'what' | 'why' | 'advanced' | null>(null);

  function handleScrollCheck() {
    const el = scrollRef.current;
    if (!el) return;
    setHasMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  }

  useEffect(() => {
    handleScrollCheck();
    window.addEventListener('resize', handleScrollCheck);
    return () => window.removeEventListener('resize', handleScrollCheck);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleScrollCheck();
  }, [openDisclosure]);

  return (
    <SlidingAnimationPageBase title={t('SmartAmp.title')} backURL={backURL}>
      <Box
        display="flex"
        flexDirection="column"
        h={`calc(${COMPONENT_MIN_HEIGHT} - ${TOP_NAV_SPACING_AFFORDANCE})`}
        position="relative"
      >
        <Box flex={1} position="relative" minH={0} overflow="hidden">
          <VStack
            ref={scrollRef}
            h="100%"
            overflowY="auto"
            onScroll={handleScrollCheck}
            px={6}
            pb={4}
            spacing={4}
            align="stretch"
          >
            <VStack
              align="center"
              justify="center"
              spacing={2}
              bg={cardBg}
              borderRadius="8px"
              borderBottomRightRadius="30px"
              px={5}
              py={4}
              minH={{ base: '351px', md: '365px' }}
            >
              <SmartDeviceStatus
                name={t('SmartAmp.name')}
                iconSrc={smartAmpIcon}
                status={status}
                statusLabel={statusLabel}
              />
              <Divider borderColor={dividerColor} />

              <Text fontSize="md" pt={2} textAlign="center" color={labelColor}>
                {t('SmartDevice.currently charging at')}
              </Text>
              <ChargingSpeedStatus
                percentage={chargePercentage}
                label={t('SmartDevice.of full speed')}
                testId="smartamp-speed-gauge"
              />
              <Text fontSize="sm" textAlign="center" color={labelColor}>
                {t('SmartAmp.charging speed description')}
              </Text>
            </VStack>

            <VStack align="stretch" spacing={0} bg={cardBg} borderRadius="8px" borderBottomRightRadius="30px">
              <DisclosureRow
                title={t('SmartAmp.q what is smartamp')}
                isOpen={openDisclosure === 'what'}
                onToggle={() => setOpenDisclosure((open) => (open === 'what' ? null : 'what'))}
                dividerColor={dividerColor}
                prefersReducedMotion={!!prefersReducedMotion}
              >
                <Text color={detailsColor}>{t('SmartAmp.a what is smartamp')}</Text>
              </DisclosureRow>
              <DisclosureRow
                title={t('SmartAmp.q why is smartamp active')}
                isOpen={openDisclosure === 'why'}
                onToggle={() => setOpenDisclosure((open) => (open === 'why' ? null : 'why'))}
                dividerColor={dividerColor}
                prefersReducedMotion={!!prefersReducedMotion}
              >
                <Text color={detailsColor}>{t('SmartAmp.a why is smartamp active')}</Text>
              </DisclosureRow>
              <DisclosureRow
                title={t('SmartDevice.advanced charging details')}
                isOpen={openDisclosure === 'advanced'}
                onToggle={() => setOpenDisclosure((open) => (open === 'advanced' ? null : 'advanced'))}
                dividerColor={dividerColor}
                prefersReducedMotion={!!prefersReducedMotion}
                hideDivider
                useChildrenBorderRadius
              >
                <AdvancedChargingDetails
                  power={currentPower}
                  maxAmps={maxAmps ?? restMaxAmps ?? null}
                  chargingAmps={chargingAmps ?? restChargingAmps ?? null}
                />
              </DisclosureRow>
            </VStack>
          </VStack>
          {hasMoreBelow && (
            <Box
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              h="56px"
              background={`linear-gradient(to bottom, transparent, ${scrollFadeColor})`}
              pointerEvents="none"
            />
          )}
        </Box>
      </Box>
    </SlidingAnimationPageBase>
  );
}
